import { Button } from '@massilia/ui'
import { Link } from '@tanstack/react-router'
import { Luggage, Plane, SearchX } from 'lucide-react'

interface NoResultsCardProps {
  departureLabel?: string
  destinationLabel?: string
  onRetryWithoutLuggage?: () => void
  withLuggage?: boolean
}

export function NoResultsCard({
  departureLabel,
  destinationLabel,
  onRetryWithoutLuggage,
  withLuggage,
}: NoResultsCardProps) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-white px-8 py-12 shadow-sm">
      <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-orange-50">
        <SearchX className="h-10 w-10 text-orange-500" />
      </div>

      <h2 className="mb-2 text-xl font-semibold text-gray-900">Aucun vol disponible</h2>

      <p className="mb-6 max-w-md text-center text-gray-600">
        {departureLabel && destinationLabel ? (
          <>
            Nous n&apos;avons trouvé aucun vol entre{' '}
            <span className="font-medium">{departureLabel}</span> et{' '}
            <span className="font-medium">{destinationLabel}</span> pour les dates et critères
            sélectionnés.
          </>
        ) : (
          <>Nous n&apos;avons trouvé aucun vol correspondant à vos critères de recherche.</>
        )}
      </p>

      {withLuggage && (
        <div className="mb-6 w-full max-w-md rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <Luggage className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">
                Votre recherche inclut le critère bagages en soute
              </p>
              <p className="mt-1 text-sm text-amber-700">
                Essayez sans ce critère pour voir plus de résultats disponibles.
              </p>
              <Button
                className="mt-3 border-amber-300 text-amber-800 hover:bg-amber-100"
                onClick={onRetryWithoutLuggage}
                type="button"
                variant="outline"
              >
                <Luggage className="mr-2 h-4 w-4" />
                Rechercher sans bagages en soute
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-8 rounded-lg bg-gray-50 p-4">
        <p className="mb-3 text-sm font-medium text-gray-700">Suggestions :</p>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-start gap-2">
            <Plane className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
            <span>Essayez des dates différentes ou plus flexibles</span>
          </li>
          <li className="flex items-start gap-2">
            <Plane className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
            <span>Vérifiez l&apos;orthographe des aéroports</span>
          </li>
          <li className="flex items-start gap-2">
            <Plane className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
            <span>Élargissez votre recherche à des aéroports proches</span>
          </li>
          <li className="flex items-start gap-2">
            <Plane className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
            <span>Modifiez le nombre de passagers ou la classe de voyage</span>
          </li>
        </ul>
      </div>

      <Link to="/recherche">
        <Button className="bg-brand-600 hover:bg-brand-700">
          <Plane className="mr-2 h-4 w-4" />
          Nouvelle recherche
        </Button>
      </Link>
    </div>
  )
}
